import { useState, useMemo } from 'react';
import Pagination from '../../../components/shared/Pagination';
import ConfirmDialog from '../../../components/shared/ConfirmDialog';
import SuccessDialog from '../../../components/shared/SuccessDialog';
import FundCommitmentsTable from '../../../components/finance/fundCommitments/FundCommitmentsTable';
import FundCommitmentFormModal from '../../../components/finance/fundCommitments/FundCommitmentFormModal';
import FundCommitmentsFilters from '../../../components/finance/fundCommitments/FundCommitmentsFilters';
import { useListFilters } from '../../../hooks/shared/useListFilters';
import { useCrudModals } from '../../../hooks/shared/useCrudModals';
import { useFundCommitments } from '../../../hooks/finance/useFundCommitments';
import { useFundCommitmentMutations } from '../../../hooks/finance/useFundCommitmentMutations';
import { useProjects } from '../../../hooks/shared/useProjects';
import { useActivePartners } from '../../../hooks/shared/useActivePartners';
import {
  PAGE,
  ACTIONS,
  PARTNER_FILTER,
  PROJECT_FILTER,
  MESSAGES,
  CONFIRM,
  SUCCESS_DIALOG,
} from './FundCommitmentsConstants';

const FundCommitmentsPage = () => {
  const [projectFilter, setProjectFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');

  const listFilters = useListFilters(10);
  const {
    filters: baseFilters,
    page,
    setPage,
    setPageToOne,
  } = listFilters;

  const apiFilters = useMemo(() => {
    const f = { ...baseFilters };
    if (projectFilter) f.project = projectFilter;
    if (partnerFilter) f.partner = partnerFilter;
    return f;
  }, [baseFilters, projectFilter, partnerFilter]);

  const { projects, isLoading: isLoadingProjects } = useProjects(
    { page: 1, pageSize: 100, isActive: true },
    { enabled: true }
  );
  const { partners, isLoading: isLoadingPartners } = useActivePartners();

  const { data, pagination, isLoading, isFetching, isError, error } =
    useFundCommitments(apiFilters, { enabled: true });

  const { createMutation, updateMutation, deleteMutation } =
    useFundCommitmentMutations();

  const crud = useCrudModals({
    createMutation,
    updateMutation,
    deleteMutation,
    getItemId: (item) => item._id,
    getItemName: (item) =>
      item.amount != null
        ? `Amount: ${item.amount} - ${item.project?.name || item.contract?.contractNo || item._id}`
        : item._id,
    messages: MESSAGES,
  });

  const {
    form,
    confirm,
    success,
    deletingId,
    deleteMutation: deleteMut,
    handleDeleteRequest,
  } = crud;

  return (
    <div className="rounded-2xl border border-cream/60 bg-white shadow-sm">
      <FundCommitmentsFilters
        title={PAGE.title}
        description={PAGE.description}
        project={projectFilter}
        onProjectChange={setProjectFilter}
        partner={partnerFilter}
        onPartnerChange={setPartnerFilter}
        onFilterChange={setPageToOne}
        projectSelectId={PROJECT_FILTER.selectId}
        partnerSelectId={PARTNER_FILTER.selectId}
        projects={projects}
        isLoadingProjects={isLoadingProjects}
        partners={partners}
        isLoadingPartners={isLoadingPartners}
        primaryAction={{
          label: ACTIONS.create,
          onClick: form.openCreateModal,
          disabled: form.isSubmitting,
        }}
      >
        <FundCommitmentsTable
          data={data}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          error={error}
          onEdit={form.openEditModal}
          onDelete={handleDeleteRequest}
          isBusy={form.isSubmitting || deleteMut.isPending}
          deletingId={deletingId}
        />
        <Pagination
          pagination={pagination}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </FundCommitmentsFilters>

      <FundCommitmentFormModal
        isOpen={form.isFormOpen}
        mode={form.formMode}
        fundCommitment={form.selectedItem}
        onSubmit={form.handleSubmitForm}
        onClose={form.handleCloseForm}
        isSubmitting={form.isSubmitting}
        serverError={form.formError}
      />

      <ConfirmDialog
        isOpen={confirm.isOpen}
        title={
          confirm.type === 'delete' ? CONFIRM.deleteTitle : CONFIRM.updateTitle
        }
        message={
          confirm.type === 'delete'
            ? CONFIRM.deleteMessage(confirm.data?.name)
            : CONFIRM.updateMessage
        }
        confirmText={
          confirm.type === 'delete' ? ACTIONS.delete : ACTIONS.edit
        }
        cancelText={ACTIONS.cancel}
        onConfirm={confirm.onConfirm}
        onCancel={confirm.onCancel}
        isLoading={confirm.isLoading}
      />

      <SuccessDialog
        isOpen={success.isOpen}
        title={
          success.variant === 'error'
            ? SUCCESS_DIALOG.errorTitle
            : SUCCESS_DIALOG.successTitle
        }
        message={success.message}
        onClose={success.onClose}
        variant={success.variant}
      />
    </div>
  );
};

export default FundCommitmentsPage;
